import { useState, useEffect, useCallback } from "react";
import { disruptionAPI } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { Check, Vote, Loader2 } from "lucide-react";

const CATEGORY_COLORS = {
  business_model: "bg-violet-50 text-violet-700 border-violet-200",
  product: "bg-indigo-50 text-indigo-700 border-indigo-200",
  service: "bg-emerald-50 text-emerald-700 border-emerald-200",
  process: "bg-amber-50 text-amber-700 border-amber-200",
  region: "bg-sky-50 text-sky-700 border-sky-200",
};

const DotVotingView = ({ boardId, maxVotes, userId }) => {
  const [ideas, setIdeas] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const fetchIdeas = useCallback(async () => {
    try {
      const res = await disruptionAPI.getIdeas(boardId);
      const allIdeas = res.data.ideas || [];
      setIdeas(allIdeas);
      // Pre-select previously voted
      const myVoted = new Set(allIdeas.filter((i) => i.votes?.includes(userId)).map((i) => i.id));
      if (myVoted.size > 0) {
        setSelectedIds(myVoted);
        setSubmitted(true);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [boardId, userId]);

  useEffect(() => { fetchIdeas(); }, [fetchIdeas]);

  const toggleVote = (ideaId) => {
    if (submitted) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(ideaId)) {
        next.delete(ideaId);
      } else if (next.size < maxVotes) {
        next.add(ideaId);
      } else {
        toast.error(`Maximum ${maxVotes} votes allowed`);
      }
      return next;
    });
  };

  const submitVotes = async () => {
    if (selectedIds.size === 0) {
      toast.error("Select at least one idea");
      return;
    }
    setSubmitting(true);
    try {
      await disruptionAPI.castVotes(boardId, [...selectedIds]);
      setSubmitted(true);
      fetchIdeas();
      toast.success(`${selectedIds.size} vote(s) submitted!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to vote");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="p-5 max-w-3xl mx-auto" data-testid="dot-voting-view">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-bold text-xl text-slate-900 flex items-center gap-2">
            <Vote className="w-5 h-5 text-violet-500" />
            Dot Voting
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            Pick your top {maxVotes} ideas from all groups
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-sm font-mono">
            {selectedIds.size}/{maxVotes} used
          </Badge>
          {!submitted && (
            <Button
              data-testid="submit-votes-btn"
              onClick={submitVotes}
              disabled={submitting || selectedIds.size === 0}
              className="bg-violet-600 hover:bg-violet-700 text-white h-9 text-sm"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Votes"}
            </Button>
          )}
        </div>
      </div>

      {submitted && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center mb-6 animate-fade-in">
          <Check className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
          <p className="text-sm font-medium text-emerald-800">Votes submitted!</p>
          <p className="text-xs text-emerald-600 mt-0.5">Results will be shown when the facilitator advances to Results.</p>
        </div>
      )}

      <div className="space-y-2">
        {ideas.map((idea, idx) => {
          const isSelected = selectedIds.has(idea.id);
          return (
            <div
              key={idea.id}
              data-testid={`vote-idea-${idea.id}`}
              onClick={() => toggleVote(idea.id)}
              className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                isSelected
                  ? "border-violet-400 bg-violet-50"
                  : "border-slate-200 bg-white hover:border-slate-300"
              } ${submitted ? "opacity-80 cursor-default" : ""}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-sm font-bold ${
                isSelected ? "bg-violet-500 text-white" : "bg-slate-100 text-slate-400"
              }`}>
                {isSelected ? <Check className="w-4 h-4" /> : idx + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-heading font-semibold text-sm text-slate-900">{idea.title}</h4>
                  <Badge className={`text-[10px] border ${CATEGORY_COLORS[idea.category] || ""}`}>
                    {idea.category?.replace("_", " ")}
                  </Badge>
                </div>
                {idea.description && (
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{idea.description}</p>
                )}
                <span className="text-[10px] text-slate-300">{idea.submitted_by_name} · {idea.group_name || "Individual"}</span>
              </div>
              <div className="text-center shrink-0">
                <div className="font-mono text-lg font-bold text-slate-700">{idea.vote_count || 0}</div>
                <div className="text-[10px] text-slate-400">votes</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DotVotingView;
